import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import {
  buildShareLink,
  channelPrefillsText,
  customerAppUrl,
  customerAppUrlForDisplay,
  shareIntentUrl,
  SHARE_CHANNELS,
  type Branding,
  type deriveShareMoments,
  type Locale,
  type ShareChannel,
  type ShareMoment,
  type TenantProfile,
} from '@kilat/shared';
import {
  Badge,
  Button,
  CheckIcon,
  ClockIcon,
  GiftIcon,
  ghostButtonStyle,
  inputStyle,
  linkRowStyle,
  MedalIcon,
  SectionHeading,
  StarIcon,
  Surface,
  TagIcon,
} from '@kilat/ui';

import { LOCALE_NATIVE_NAMES, useI18n } from '../lib/use-i18n.js';
import { useMyRole } from '../lib/use-my-role.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { logger } from '../lib/logger.js';
import { sectionGridStyle } from '../lib/layout.js';
import { callAiRewriteShareCaption } from '../lib/callables/ai.js';
import { momentCopy } from '../lib/share-captions.js';
import {
  canvasToPngBlob,
  renderShareImage,
  SHARE_IMAGE_SIZES,
  type ShareImageFormat,
  type ShareImageSpec,
} from '../lib/share-image.js';

/**
 * The Share kit's working area (Share & grow): the list of moments on one side,
 * and for the chosen one the caption, image and share actions on the other.
 * The route (`routes/marketing-share.tsx`) loads the data and owns the page's
 * loading / error / empty states; everything here assumes there is at least
 * one real moment to work with.
 */

export interface KitData {
  readonly profile: TenantProfile;
  readonly branding: Branding;
  readonly promotions: Parameters<typeof deriveShareMoments>[0]['promotions'];
  readonly washCount: number | undefined;
  readonly referralsEnabled: boolean;
}

const MOMENT_ICON: Record<ShareMoment['kind'], (p: { size?: number }) => React.ReactElement> = {
  promotion: TagIcon,
  milestone: MedalIcon,
  openNow: ClockIcon,
  quietNow: CheckIcon,
  referral: GiftIcon,
  review: StarIcon,
};

export function ShareKit({
  tenantId,
  data,
  moments,
}: {
  readonly tenantId: string;
  readonly data: KitData;
  readonly moments: readonly ShareMoment[];
}) {
  const { t, tIn, locale: uiLocale, supportedLocales } = useI18n();
  const [selectedId, setSelectedId] = useState(moments[0]?.id ?? '');
  const selected = moments.find((m) => m.id === selectedId) ?? moments[0];
  const [postLocale, setPostLocale] = useState<Locale>(uiLocale);
  const shop = data.profile.businessName;

  return (
    <div style={sectionGridStyle}>
      <Surface as="section" aria-labelledby="moments-title">
        <SectionHeading
          trailing={
            <Badge tone="success">
              {t('dashboard.shareKit.momentsCount', { n: moments.length })}
            </Badge>
          }
        >
          <span id="moments-title">{t('dashboard.shareKit.momentsHeading')}</span>
        </SectionHeading>
        <div
          role="radiogroup"
          aria-labelledby="moments-title"
          style={{ display: 'grid', gap: 'var(--space-2)' }}
        >
          {moments.map((m) => {
            const Icon = MOMENT_ICON[m.kind];
            const active = m.id === selected?.id;
            const preview = momentCopy((k, v) => tIn(postLocale, k, v), m, {
              shop,
              locale: postLocale,
            });
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSelectedId(m.id)}
                className="k-press"
                style={{
                  ...momentButton,
                  borderColor: active ? 'var(--color-primary)' : 'var(--color-border)',
                  background: active ? 'var(--tint-subtle)' : 'var(--color-surface)',
                }}
              >
                <span aria-hidden style={{ color: 'var(--color-primary)', flexShrink: 0 }}>
                  <Icon size={22} />
                </span>
                <span style={{ display: 'grid', gap: 'var(--space-1)', minWidth: 0 }}>
                  <span style={{ ...mutedText, fontWeight: 600 }}>
                    {t(`dashboard.shareKit.kinds.${m.kind}`)}
                  </span>
                  <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {preview.headline}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </Surface>

      {selected ? (
        <Composer
          key={`${selected.id}:${postLocale}`}
          tenantId={tenantId}
          data={data}
          moment={selected}
          postLocale={postLocale}
          onLocale={setPostLocale}
          locales={supportedLocales}
        />
      ) : null}
    </div>
  );
}

type AiState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'working' }
  | { readonly kind: 'done'; readonly previous: string }
  | { readonly kind: 'notSetUp' }
  | { readonly kind: 'failed' };

type Notice = { readonly key: string; readonly vars?: Record<string, string> } | null;

function Composer({
  tenantId,
  data,
  moment,
  postLocale,
  onLocale,
  locales,
}: {
  readonly tenantId: string;
  readonly data: KitData;
  readonly moment: ShareMoment;
  readonly postLocale: Locale;
  readonly onLocale: (l: Locale) => void;
  readonly locales: readonly Locale[];
}) {
  const { t, tIn } = useI18n();
  const brand = usePlatformBrand();
  const role = useMyRole();
  const canUseAi = role === 'owner' || role === 'manager';
  const shop = data.profile.businessName;
  const copy = useMemo(
    () => momentCopy((k, v) => tIn(postLocale, k, v), moment, { shop, locale: postLocale }),
    [tIn, postLocale, moment, shop],
  );
  const [caption, setCaption] = useState(copy.caption);
  const [ai, setAi] = useState<AiState>({ kind: 'idle' });
  const [aiChannel, setAiChannel] = useState<ShareChannel>('whatsapp');
  const [format, setFormat] = useState<ShareImageFormat>('square');
  const [imageState, setImageState] = useState<'drawing' | 'ready' | 'failed'>('drawing');
  const [notice, setNotice] = useState<Notice>(null);
  const [withLogo, setWithLogo] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const appBase = customerAppUrl(brand.domains, data.profile.slug);
  const linkFor = (source: Parameters<typeof buildShareLink>[1]) =>
    buildShareLink(appBase, source, moment.id);

  const spec = useMemo<ShareImageSpec>(
    () => ({
      format,
      shopName: shop,
      eyebrow: copy.eyebrow,
      headline: copy.headline,
      body: copy.body,
      footer: customerAppUrlForDisplay(brand.domains, data.profile.slug).replace(/\/$/, ''),
      colours: {
        background: data.branding.colours.primary,
        foreground: data.branding.colours.primaryFg,
        accent: data.branding.colours.accent,
        accentForeground: data.branding.colours.accentFg,
      },
      logoUrl: withLogo ? (data.branding.logoUrl ?? null) : null,
      fontFamily:
        typeof document !== 'undefined' ? getComputedStyle(document.body).fontFamily : 'sans-serif',
    }),
    [format, shop, copy, brand.domains, data, withLogo],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    setImageState('drawing');
    // Draw off-screen and copy across only if this render is still current:
    // the logo loads asynchronously, so a slower earlier render could
    // otherwise finish last and paint the previous moment over this one.
    const paint = (source: HTMLCanvasElement) => {
      canvas.width = source.width;
      canvas.height = source.height;
      canvas.getContext('2d')?.drawImage(source, 0, 0);
    };
    // Two passes. The first draws immediately with the monogram — no network —
    // so the operator never stares at an empty white box (a slow or broken
    // logo URL used to hold the whole image back for up to four seconds). The
    // second redraws with the real logo if there is one and it loads.
    const quick = document.createElement('canvas');
    renderShareImage(quick, { ...spec, logoUrl: null })
      .then(async () => {
        if (cancelled) return;
        paint(quick);
        setImageState('ready');
        if (!spec.logoUrl) return;
        const full = document.createElement('canvas');
        await renderShareImage(full, spec);
        if (!cancelled) paint(full);
      })
      .catch((e: unknown) => {
        logger.warn('share image render failed', { error: String(e) });
        if (!cancelled) setImageState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [spec]);

  async function pngFile(): Promise<File | null> {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    let blob = await canvasToPngBlob(canvas);
    if (!blob && withLogo && spec.logoUrl) {
      // The logo tainted the canvas; draw again without it.
      setWithLogo(false);
      await renderShareImage(canvas, { ...spec, logoUrl: null });
      blob = await canvasToPngBlob(canvas);
    }
    if (!blob) return null;
    return new File([blob], `${data.profile.slug}-${moment.id}-${format}.png`, {
      type: 'image/png',
    });
  }

  async function download(): Promise<boolean> {
    const file = await pngFile();
    if (!file) {
      setImageState('failed');
      return false;
    }
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  }

  async function copyText(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      logger.warn('share kit copy failed', { error: String(e) });
      return false;
    }
  }

  const withLink = (source: Parameters<typeof buildShareLink>[1]) =>
    `${caption.trim()}\n\n${linkFor(source)}`;

  async function shareToAppOnly(channel: 'instagram' | 'tiktok'): Promise<void> {
    const downloaded = await download();
    const copied = await copyText(withLink(channel));
    if (downloaded || copied) {
      setNotice({
        key: 'dashboard.shareKit.appOnlyHint',
        vars: { app: t(`dashboard.reports.sources.source.${channel}`) },
      });
    }
  }

  async function shareNative(): Promise<void> {
    const file = await pngFile();
    const text = caption.trim();
    const url = linkFor('other');
    try {
      if (file && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: `${text}\n\n${url}` });
      } else {
        await navigator.share({ text, url });
      }
    } catch (e) {
      // Dismissing the share sheet is not an error worth showing.
      if ((e as { name?: string }).name !== 'AbortError') {
        logger.warn('native share failed', { error: String(e) });
      }
    }
  }

  async function rewrite(): Promise<void> {
    const before = caption;
    setAi({ kind: 'working' });
    try {
      const res = await callAiRewriteShareCaption({
        tenantId,
        caption: before,
        locale: postLocale,
        channel: aiChannel,
      });
      if (res.provider === 'noop') {
        setAi({ kind: 'notSetUp' });
        return;
      }
      if (!res.caption) {
        setAi({ kind: 'failed' });
        return;
      }
      setCaption(res.caption);
      setAi({ kind: 'done', previous: before });
    } catch (e) {
      logger.warn('aiRewriteShareCaption failed', { error: String(e) });
      setAi({ kind: 'failed' });
    }
  }

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const size = SHARE_IMAGE_SIZES[format];

  return (
    <Surface
      as="section"
      aria-labelledby="composer-title"
      style={{ display: 'grid', gap: 'var(--space-5)' }}
    >
      <SectionHeading>
        <span id="composer-title">{t('dashboard.shareKit.composerHeading')}</span>
      </SectionHeading>

      <label style={field}>
        <span style={labelText}>{t('dashboard.shareKit.languageLabel')}</span>
        <select
          value={postLocale}
          onChange={(e) => onLocale(e.target.value as Locale)}
          style={inputStyle}
        >
          {locales.map((l) => (
            <option key={l} value={l}>
              {LOCALE_NATIVE_NAMES[l]}
            </option>
          ))}
        </select>
      </label>

      <label style={field}>
        <span style={labelText}>{t('dashboard.shareKit.captionLabel')}</span>
        <textarea
          value={caption}
          rows={5}
          maxLength={600}
          onChange={(e) => {
            setCaption(e.target.value);
            if (ai.kind !== 'working') setAi({ kind: 'idle' });
          }}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
        />
        <span style={mutedText}>{t('dashboard.shareKit.captionHint')}</span>
      </label>

      <div style={linkRowStyle}>
        {caption !== copy.caption ? (
          <Button
            variant="quiet"
            onClick={() => {
              setCaption(copy.caption);
              setAi({ kind: 'idle' });
            }}
          >
            {t('dashboard.shareKit.resetCaption')}
          </Button>
        ) : null}
        {canUseAi ? (
          <>
            <label style={{ ...linkRowStyle, alignItems: 'center' }}>
              <span style={labelText}>{t('dashboard.shareKit.ai.toneLabel')}</span>
              <select
                value={aiChannel}
                onChange={(e) => setAiChannel(e.target.value as ShareChannel)}
                style={{ ...inputStyle, width: 'auto' }}
              >
                {SHARE_CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {t(`dashboard.reports.sources.source.${c}`)}
                  </option>
                ))}
              </select>
            </label>
            <Button
              variant="secondary"
              disabled={ai.kind === 'working' || caption.trim().length < 4}
              onClick={() => void rewrite()}
            >
              {ai.kind === 'working'
                ? t('dashboard.shareKit.ai.working')
                : t('dashboard.shareKit.ai.button')}
            </Button>
          </>
        ) : (
          <span style={mutedText}>{t('dashboard.shareKit.ai.ownerOnly')}</span>
        )}
      </div>
      {ai.kind === 'done' ? (
        <p role="status" style={{ ...mutedText, ...linkRowStyle, alignItems: 'center' }}>
          {t('dashboard.shareKit.ai.done')}
          <Button
            variant="quiet"
            onClick={() => {
              setCaption(ai.previous);
              setAi({ kind: 'idle' });
            }}
          >
            {t('dashboard.shareKit.ai.undo')}
          </Button>
        </p>
      ) : null}
      {ai.kind === 'notSetUp' ? (
        <p role="status" style={mutedText}>
          {t('dashboard.shareKit.ai.notSetUp')}
        </p>
      ) : null}
      {ai.kind === 'failed' ? (
        <p role="alert" style={{ ...mutedText, color: 'var(--color-danger)' }}>
          {t('dashboard.shareKit.ai.failed')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <div style={{ ...linkRowStyle, justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={labelText}>{t('dashboard.shareKit.imageHeading')}</span>
          <div
            role="radiogroup"
            aria-label={t('dashboard.shareKit.imageHeading')}
            style={linkRowStyle}
          >
            {(['square', 'story'] as const).map((f) => (
              <Button
                key={f}
                role="radio"
                aria-checked={format === f}
                variant={format === f ? 'primary' : 'secondary'}
                onClick={() => setFormat(f)}
              >
                {f === 'square'
                  ? t('dashboard.shareKit.formatSquare')
                  : t('dashboard.shareKit.formatStory')}
              </Button>
            ))}
          </div>
        </div>
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: format === 'story' ? '18rem' : '26rem',
            justifySelf: 'center',
          }}
        >
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={t('dashboard.shareKit.imageAlt')}
            data-testid="share-image"
            width={size.width}
            height={size.height}
            style={{
              width: '100%',
              height: 'auto',
              aspectRatio: `${size.width} / ${size.height}`,
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-2)',
              display: 'block',
            }}
          />
          {imageState === 'drawing' ? (
            <span
              role="status"
              style={{ ...mutedText, display: 'block', marginTop: 'var(--space-2)' }}
            >
              {t('dashboard.shareKit.imageRendering')}
            </span>
          ) : null}
        </div>
        {imageState === 'failed' ? (
          <p role="alert" style={{ ...mutedText, color: 'var(--color-danger)' }}>
            {t('dashboard.shareKit.imageFailed')}
          </p>
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <span style={labelText}>{t('dashboard.shareKit.shareHeading')}</span>
        <div style={linkRowStyle}>
          {SHARE_CHANNELS.map((channel) => {
            const name = t(`dashboard.reports.sources.source.${channel}`);
            const href = shareIntentUrl(channel, {
              text: caption.trim(),
              url: linkFor(channel),
            });
            if (channel === 'instagram' || channel === 'tiktok' || !href) {
              return (
                <Button
                  key={channel}
                  variant="secondary"
                  onClick={() => void shareToAppOnly(channel === 'tiktok' ? 'tiktok' : 'instagram')}
                >
                  {name}
                </Button>
              );
            }
            return (
              <a
                key={channel}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="k-press"
                style={channelLink}
                data-channel={channel}
                onClick={() => {
                  if (!channelPrefillsText(channel)) {
                    // Facebook ignores pre-filled text: carry the caption on
                    // the clipboard and say so.
                    void copyText(caption.trim()).then((ok) => {
                      if (ok) setNotice({ key: 'dashboard.shareKit.facebookHint' });
                    });
                  }
                }}
              >
                {name}
              </a>
            );
          })}
        </div>
        <div style={linkRowStyle}>
          <Button
            variant="primary"
            onClick={() =>
              void copyText(withLink('link')).then((ok) => {
                if (ok) setNotice({ key: 'dashboard.shareKit.copied' });
              })
            }
          >
            {t('dashboard.shareKit.copyCaption')}
          </Button>
          <Button variant="secondary" onClick={() => void download()}>
            {t('dashboard.shareKit.downloadImage')}
          </Button>
          {canNativeShare ? (
            <Button variant="secondary" onClick={() => void shareNative()}>
              {t('dashboard.shareKit.shareMore')}
            </Button>
          ) : null}
        </div>
        {notice ? (
          <p role="status" style={{ ...mutedText, color: 'var(--color-on-surface)' }}>
            {t(notice.key, notice.vars)}
          </p>
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
        <span style={labelText}>{t('dashboard.shareKit.linkLabel')}</span>
        <code
          style={{ overflowWrap: 'anywhere', fontSize: 'var(--text-meta)' }}
          data-testid="share-link"
        >
          {linkFor('link')}
        </code>
        <span style={mutedText}>{t('dashboard.shareKit.linkHint')}</span>
      </div>
    </Surface>
  );
}

// ── styles ─────────────────────────────────────────────────────────────

export const mutedText: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const labelText: CSSProperties = { fontSize: 'var(--text-meta)', fontWeight: 600 };

const field: CSSProperties = { display: 'grid', gap: 'var(--space-1)' };

const momentButton: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  width: '100%',
  minHeight: 'var(--touch-target)',
  padding: 'var(--space-3) var(--space-4)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-on-surface)',
  textAlign: 'start',
  font: 'inherit',
  cursor: 'pointer',
};

const channelLink: CSSProperties = { ...ghostButtonStyle, textDecoration: 'none' };
